import React, { useState, useEffect, useMemo } from 'react';
import Navbar from '../../../components/Backoffice/Navbar/Navbar';
import { useNavigationContext } from '../../../contexts/NavigationContext';
import { privateFetch } from '../../../utils/apiHelper';
import { confirmDialog } from 'primereact/confirmdialog';
import ResidencyInformation from './ResidencyInformation';
import ResidencyEvents from './ResidencyEventList';
import ManageResidency from './ManageResidency';
import ManageResidencyEvent from './ManageResidencyEvent';
import ResidencyImageList from './ResidencyImageList';
import ResidencyAddress from './ResidencyAddress';
import { useAccountContext } from '../../../contexts/AccountContext';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import Section from '../../../components/Backoffice/Display/Section';
import ResidencyAccount from './ResidencyAccount';
import { useToastContext } from '../../../contexts/ToastContext';
import ManageResidencyAccount from './ManageResidencyAccount';

function ResidencyPage() {
  const { navigationContext, setNavigationContext, openResidencyDetail } = useNavigationContext();
  const { showToast } = useToastContext();
  const { isAdmin } = useAccountContext();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [residency, setResidency] = useState(undefined);
  const [residencyPanelOpen, setResidencyPanelOpen] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);

  // Event
  const [event, setEvent] = useState(undefined);
  const [eventPanelOpen, setEventPanelOpen] = useState(false);

  // Account
  const [accountPanelOpen, setAccountPanelOpen] = useState(false);

  const isHost = useMemo(() => ['long', 'short'].includes(residency?.type), [residency]);
  const isLong = useMemo(() => ['long'].includes(residency?.type), [residency]);

  useEffect(() => {
    if (navigationContext.activeResidency && navigationContext.activeResidency.id) {
      fetchResidencyDetail();
    }
  }, [navigationContext.activeResidency]);

  const fetchResidencyDetail = async () => {
    await privateFetch('GET', `/residency/${navigationContext.activeResidency.id}/`).then((res) => {
      if (res) setResidency(res);
    });
  };

  const updateResidency = async (residencyForm) => {
    setLoading(true);
    await privateFetch(
      'PUT',
      `/residency/${residency.id}/`,
      `Le séjour ${residencyForm.label} a bien été modifié.`,
      JSON.stringify(residencyForm)
    ).then(() => {
      setResidencyPanelOpen(false);
      setLoading(false);
      fetchResidencyDetail();
    });
  };

  const createEvent = async (eventForm) => {
    setLoading(true);
    await privateFetch(
      'POST',
      `/residency/${residency.id}/event/`,
      "L'évènement a bien été ajouté",
      JSON.stringify(eventForm)
    ).then(() => {
      fetchResidencyDetail();
      setLoading(false);
      setEventPanelOpen(false);
    });
  };

  const updateEvent = async (eventForm) => {
    setLoading(true);
    await privateFetch(
      'PUT',
      `/residency/${residency.id}/event/${event.id}/`,
      `L'évènement ${eventForm.label} a bien été modifié.`,
      JSON.stringify(eventForm)
    ).then(() => {
      setEventPanelOpen(false);
      setLoading(false);
      fetchResidencyDetail();
    });
  };

  const deleteEvent = async (targetEvent) => {
    setLoading(true);
    await privateFetch(
      'DELETE',
      `/residency/${residency.id}/event/${targetEvent.id}/`,
      "L'évènement a bien été supprimé."
    ).then(() => {
      setLoading(false);
      fetchResidencyDetail();
    });
  };

  const duplicateResidency = async (residencyForm) => {
    setLoading(true);
    await privateFetch(
      'POST',
      `/residency/`,
      'Le séjour a bien été dupliqué',
      JSON.stringify(residencyForm)
    ).then((res) => {
      setLoading(false);
      setResidencyPanelOpen(false);
      openResidencyDetail({ id: res.id, label: res.label });
    });
  };

  const deleteResidency = async () => {
    setLoading(true);
    await privateFetch(
      'DELETE',
      `/residency/${residency.id}/`,
      'La résidence a bien été supprimée.'
    ).then(() => {
      setLoading(false);
      setNavigationContext({
        ...navigationContext,
        activeApp: null,
        activeResidency: { label: '', id: null }
      });
      navigate('/private/dashboard');
    });
  };

  const handleResidencyEventUpdate = (targetEvent) => {
    setEvent(targetEvent);
    setEventPanelOpen(true);
    setIsUpdate(true);
  };

  const handleResidencyEventDuplicate = (targetEvent) => {
    setEvent(targetEvent);
    setEventPanelOpen(true);
    setIsUpdate(false);
  };

  const confirmEventDeletion = (targetEvent) => {
    setEventPanelOpen(false);
    confirmDialog({
      message: `Êtes-vous sûr de vouloir supprimer l'évènement ${targetEvent.label} ?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui',
      rejectLabel: 'Annuler',
      accept: () => deleteEvent(targetEvent)
    });
  };

  const confirmAccountDeletion = () => {
    confirmDialog({
      message: `Êtes-vous sûr de vouloir supprimer le compte ${residency?.custom_user?.email} ?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui',
      rejectLabel: 'Annuler',
      accept: deleteAccount
    });
  };

  const createAccount = async () => {
    setLoading(true);
    await privateFetch(
      'POST',
      `/residency/${residency.id}/account/`,
      'Le compte a bien été créé, et le mail de connexion envoyé.'
    ).then(() => {
      fetchResidencyDetail();
      setLoading(false);
      setAccountPanelOpen(false);
    });
  };

  const reasignAccount = async (targetCustomUser) => {
    setLoading(true);
    await privateFetch(
      'POST',
      `/residency/${residency.id}/account/${targetCustomUser.id}/reassign/`,
      "Le compte a bien été lié à la nouvelle résidence, et un mail d'information a été envoyé."
    ).then(() => {
      fetchResidencyDetail();
      setLoading(false);
      setAccountPanelOpen(false);
    });
  };

  const deleteAccount = async () => {
    setLoading(true);
    await privateFetch(
      'DELETE',
      `/cu/${residency.custom_user.id}/`,
      'Le compte a bien été supprimé'
    ).then(() => {
      setLoading(false);
      fetchResidencyDetail();
    });
  };

  const confirmAskAdress = () => {
    if (!residency?.email) {
      showToast(
        'error',
        `Impossible de demander la nouvelle adresse postale. Merci de renseigner une adresse mail dans les informations générales de la résidence.`
      );
    } else {
      confirmDialog({
        message: `Un mail sera envoyé à ${residency?.email} afin de renseigner une nouvelle adresse postale. `,
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Envoyer',
        rejectLabel: 'Annuler',
        accept: handleAskAddress
      });
    }
  };

  const handleAskAddress = async () => {
    setLoading(true);
    await privateFetch(
      'POST',
      `/residency/${residency.id}/add-location/`,
      'Le mail a bien été envoyé'
    ).then((response) => {
      if (response) {
        setLoading(false);
        fetchResidencyDetail(residency.id);
      }
    });
  };

  const handleCreateAddress = () => {
    window.open(`/#/essaimage/?token=${residency.location_token}`, '_blank');
  };

  const copyAccountToken = () => {
    const url = `https://le-registre.hotelpasteur.fr/#/password-setup/${residency.custom_user.password_token}/`;
    navigator.clipboard.writeText(url);
    showToast('success', 'Lien copié dans le presse-papier');
  };

  useEffect(() => {
    const scrollableContainer = document.querySelector('.page-wrapper');

    scrollableContainer.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [residency]);

  return (
    <div className={`h-screen w-screen bg-slate-50 flex flex-col backoffice-scope`}>
      <Navbar />
      {residency && (
        <div className="page-wrapper !pb-40">
          <ResidencyInformation
            loading={loading}
            residency={residency}
            handleResidencyUpdate={() => setResidencyPanelOpen(true)}
          />
          <ResidencyImageList residency={residency} />

          {isLong && isAdmin && (
            <ResidencyAccount
              loading={loading}
              account={residency.custom_user}
              handleCreateAccount={() => setAccountPanelOpen(true)}
              handleDeleteAccount={confirmAccountDeletion}
              handleCopyAccountToken={copyAccountToken}
            />
          )}
          {isLong && isAdmin && (
            <ResidencyAddress
              loading={loading}
              residency={residency}
              handleAskAddress={confirmAskAdress}
              handleCreateAddress={handleCreateAddress}
            />
          )}

          {isHost && (
            <ResidencyEvents
              loading={loading}
              residency={residency}
              handleResidencyEventCreate={() => {
                setEvent(undefined);
                setEventPanelOpen(true);
                setIsUpdate(false);
              }}
              handleResidencyEventUpdate={handleResidencyEventUpdate}
              handleResidencyEventDelete={confirmEventDeletion}
              handleResidencyEventDuplicate={handleResidencyEventDuplicate}
            />
          )}
          {isAdmin && (
            <Section label="Autres actions">
              <div className="flex flex-row gap-x-3">
                <Button
                  loading={loading}
                  label="Supprimer le séjour"
                  onClick={() => deleteResidency()}
                  icon="pi pi-trash"
                  severity="danger"
                  outlined
                  className="w-fit"
                />
                <Button
                  loading={loading}
                  label="Dupliquer le séjour"
                  onClick={() => {
                    setResidencyPanelOpen(true);
                    setIsDuplicate(true);
                  }}
                  icon="pi pi-clone"
                  severity="success"
                  outlined
                  className="w-fit"
                />
              </div>
            </Section>
          )}
        </div>
      )}

      <ManageResidency
        key={'residency'}
        loading={loading}
        residency={residency}
        isOpen={residencyPanelOpen}
        handleClose={() => {
          setResidencyPanelOpen(false);
        }}
        updateResidency={updateResidency}
        isDuplicate={isDuplicate}
        createResidency={duplicateResidency}
      />

      <ManageResidencyAccount
        key={'account'}
        loading={loading}
        residency={residency}
        isOpen={accountPanelOpen}
        handleClose={() => {
          setAccountPanelOpen(false);
        }}
        createAccount={createAccount}
        reasignAccount={reasignAccount}
      />

      {residency && (
        <ManageResidencyEvent
          key={'event'}
          loading={loading}
          residency={residency}
          event={event}
          isOpen={eventPanelOpen}
          handleClose={() => {
            setEventPanelOpen(false);
          }}
          updateEvent={updateEvent}
          createEvent={createEvent}
          isUpdate={isUpdate}
        />
      )}
    </div>
  );
}

export default ResidencyPage;
